import UpdateAvatarModal from '../update-avatar-modal/update-avatar-modal';
import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ErrorMessage, useAlert } from '@employ-engage/common-components';
import classNames from 'classnames';
import { User } from '@employ-engage/data';
import { useTranslation } from 'react-i18next';
import { useIsSupportSession } from '../support-access/use-support-session';

export interface PersonalInformationFormProps {
  user: User;
}

export function PersonalInformationForm({
  user,
}: PersonalInformationFormProps) {
  const { t } = useTranslation(['common']);
  const isSupportSession = useIsSupportSession();
  const { Alert: ProfileSavedAlert } = useAlert({
    type: 'success',
    title: t('Saved'),
    message: t('Your profile has been updated.'),
  });
  const [openUpdateAvatarModal, setOpenUpdateAvatarModal] = useState(false);

  const nameParts = user?.displayName?.split(' ') || [];

  const {
    handleSubmit,
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    dirty,
  } = useFormik({
    initialValues: {
      thumbnailUrl: user?.photoURL || '',
      firstName: nameParts[0] || '',
      lastName: nameParts[nameParts.length - 1] || '',
      email: user?.email || '',
    },
    onSubmit: (values) => {
      // const auth = getAuth();
      // updateProfile(auth.currentUser!, {
      //   photoURL: values.thumbnailUrl,
      //   displayName: `${values.firstName} ${values.lastName}`,
      // }).then(() => {
      //   fireAlert(3000);
      // });
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required(t('Required')),
      lastName: Yup.string().required(t('Required')),
      email: Yup.string()
        .email('Invalid email address')
        .required(t('Required')),
    }),
  });

  const uploadAvatar = (e: any) => {
    // const storage = getStorage();
    // const thumbnailRef = ref(
    //   storage,
    //   `thumbnails/${user.id}.${e.target.files[0].type.split('/')[1]}`
    // );

    // uploadBytes(thumbnailRef, e.target.files[0]).then((snapshot) => {
    //   getDownloadURL(snapshot.ref).then((url) => {
    //     setFieldValue('thumbnailUrl', url);
    //     setOpenUpdateAvatarModal(false);
    //   });
    // });
  };

  const isSubmitDisabled = Object.keys(errors).length > 0 || !dirty;

  return (
    <form className="md:col-span-2" onSubmit={handleSubmit}>
      <div className="w-full grid grid-cols-1 gap-x-6 gap-y-8 sm:max-w-xl sm:grid-cols-6">
        <div className="col-span-full flex items-center gap-x-8">
          <img
            src={
              values.thumbnailUrl ||
              `https://ui-avatars.com/api/?name=${
                user?.displayName || ''
              }&background=aaa&color=fff&size=128`
            }
            alt={user?.displayName || ''}
            className="h-24 w-24 flex-none rounded-lg object-cover"
          />
          <div>
            <button
              onClick={() => setOpenUpdateAvatarModal(true)}
              type="button"
              className="bg-stone-50 rounded-lg px-3 py-2 text-sm font-semibold shadow-sm hover:bg-light-primary hover:text-white text-light-primaryText"
            >
              {t('Change avatar')}
            </button>
            <p className="mt-2 text-xs leading-5 text-light-secondaryText">
              {t('JPG, GIF or PNG. 1MB max.')}
            </p>
          </div>
        </div>
        <UpdateAvatarModal
          open={openUpdateAvatarModal}
          setOpen={setOpenUpdateAvatarModal}
          onUpload={uploadAvatar}
        />

        <div className="sm:col-span-3 text-light-primaryText">
          <label
            htmlFor="firstName"
            className="block text-sm font-medium leading-6"
          >
            {t('First name')}
          </label>
          <div className="mt-2">
            <input
              type="text"
              name="firstName"
              id="firstName"
              value={values.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              autoComplete="given-name"
              className="block w-full rounded-md border-0 bg-white/80 py-1.5 text-light-secondaryText shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6"
            />
            {errors.firstName && touched.firstName && (
              <ErrorMessage
                errors={errors.firstName}
                touched={touched.firstName}
              />
            )}
          </div>
        </div>

        <div className="sm:col-span-3">
          <label
            htmlFor="lastName"
            className="block text-sm font-medium leading-6 text-light-primaryText"
          >
            {t('Last name')}
          </label>
          <div className="mt-2">
            <input
              type="text"
              name="lastName"
              id="lastName"
              value={values.lastName}
              onChange={handleChange}
              onBlur={handleBlur}
              autoComplete="family-name"
              className="block w-full rounded-md border-0 bg-white/80 py-1.5 text-light-secondaryText shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6"
            />
            {errors.lastName && touched.lastName && (
              <ErrorMessage
                errors={errors.lastName}
                touched={touched.lastName}
              />
            )}
          </div>
        </div>

        <div className="col-span-full">
          <label
            htmlFor="email"
            className="block text-sm font-medium leading-6 text-light-primaryText"
          >
            {t('Email address')}
          </label>
          <div className="mt-2">
            <input
              id="email"
              name="email"
              type="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              autoComplete="email"
              // The sign-in email is off limits to a super admin acting as
              // this user; the API refuses the change as well.
              disabled={isSupportSession}
              className="block w-full rounded-md border-0 bg-white/80 py-1.5 text-light-secondaryText shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-indigo-500 disabled:opacity-60 sm:text-sm sm:leading-6"
            />
            {isSupportSession && (
              <p className="mt-1 text-sm text-light-secondaryText">
                {t('not-available-in-support-session')}
              </p>
            )}
            {errors.email && touched.email && (
              <ErrorMessage errors={errors.email} touched={touched.email} />
            )}
          </div>
        </div>
      </div>

      <div className="mt-8 flex">
        <button
          type="submit"
          className={classNames(
            'rounded-md bg-indigo-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500',
            {
              'opacity-50 cursor-not-allowed': isSubmitDisabled,
            }
          )}
          disabled={isSubmitDisabled}
        >
          {t('Save')}
        </button>
      </div>
      <ProfileSavedAlert />
    </form>
  );
}

export default PersonalInformationForm;
